import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type KeyboardEvent,
} from 'react'

import { ORDMarkingFormData } from '@synapex/prisma/client'

import { CopyTextIcon, EditPencilIcon } from '@/shared/assets'
import { useCopyToClipboard } from '@/shared/hooks'
import { Input, ShadcnButton, Toast } from '@/shared/ui'
import clsx from 'clsx'

type MarkingMediaTextBlockProps = {
    className?: string
    markMediaText: ORDMarkingFormData['mark_media_text']
    onSubmit: (value: string) => void
    disabled?: boolean
}

export const MarkingMediaTextBlock = ({
    className,
    markMediaText,
    onSubmit,
    disabled,
}: MarkingMediaTextBlockProps) => {
    const [isEditingMediaText, setIsEditingMediaText] = useState(false)
    const [draftMediaText, setDraftMediaText] = useState(markMediaText ?? '')

    const inputRef = useRef<HTMLInputElement>(null)

    const { copy, toastVisible, toastMessage, hideToast } = useCopyToClipboard()

    useEffect(() => {
        if (!isEditingMediaText) {
            setDraftMediaText(markMediaText ?? '')
        }
    }, [markMediaText, isEditingMediaText])

    useEffect(() => {
        if (isEditingMediaText) {
            inputRef.current?.focus()
            inputRef.current?.select()
        }
    }, [isEditingMediaText])

    const finishEditingMediaText = useCallback(() => {
        if (!isEditingMediaText) {
            return
        }

        setIsEditingMediaText(false)

        if (draftMediaText !== (markMediaText ?? '')) {
            onSubmit(draftMediaText)
        }
    }, [draftMediaText, isEditingMediaText, markMediaText, onSubmit])

    const handleEditMediaText = () => {
        if (isEditingMediaText) {
            finishEditingMediaText()
        } else {
            setDraftMediaText(markMediaText ?? '')
            setIsEditingMediaText(true)
        }
    }

    const handleMediaTextKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            finishEditingMediaText()
        }
    }

    return (
        <div className={className}>
            <Input
                ref={inputRef}
                placeholder="Текст для маркировки"
                value={
                    isEditingMediaText
                        ? draftMediaText
                        : (markMediaText ?? '').replace(/\n/g, ' ')
                }
                disabled={!isEditingMediaText}
                innerClassName={`h-[32px] text-[#7A888F] ${
                    isEditingMediaText ? 'cursor-text' : 'cursor-pointer'
                }`}
                shouldShowClearButton={false}
                onChange={setDraftMediaText}
                onKeyDown={handleMediaTextKeyDown}
                onBlur={() => finishEditingMediaText()}
            />
            <ShadcnButton
                className="h-auto p-0"
                onClick={() =>
                    copy(
                        isEditingMediaText
                            ? draftMediaText
                            : (markMediaText ?? '')
                    )
                }
            >
                <img src={CopyTextIcon} className="h-4 w-[14px]" />
            </ShadcnButton>
            <ShadcnButton
                variant="ghost"
                onClick={handleEditMediaText}
                className="h-fit p-0"
                disabled={disabled}
            >
                <EditPencilIcon
                    className={clsx('h-4 w-4', {
                        invisible: disabled,
                    })}
                />
            </ShadcnButton>

            <Toast
                message={toastMessage}
                visible={toastVisible}
                onClose={hideToast}
                greenSwooshIcon={true}
            />
        </div>
    )
}
